/**
 * SpawnBatchDetailModal Component
 *
 * Detail view for a single spawn batch with individual bag tracking.
 * Status changes via action buttons. Bags are the visual centrepiece.
 */

import { useMemo, useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { InlineEditableField } from '@/components/ui/inline-editable-field'
import { InlineSelectField } from '@/components/ui/inline-select-field'
import { useSpawnMutations } from '@/hooks/useSpawnBatches'
import { useSpawnBatchBags, useSpawnBagMutations } from '@/hooks/useSpawnBatchBags'
import { useLiquidCultures } from '@/hooks/useLC'
import { useProcessRecipes } from '@/hooks/useProcessRecipes'
import RecipeModal from '@/components/production/recipes/RecipeModal'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { parseDateString } from '@/utils/dateUtils'

// Bag status badge variants
const bagStatusVariants = {
  available: 'default',
  used: 'secondary',
  contaminated: 'destructive',
  discarded: 'outline'
}

// Status transitions available from each state (for action buttons)
// Removed 'used' and 'contaminated' - bags are marked individually
const statusTransitions = {
  planned: ['colonising'],
  colonising: ['colonised'],
  colonised: ['storage'],
  storage: [],
  used: [],
  contaminated: ['discarded'],
  discarded: []
}

// Status button labels
const statusButtonLabels = {
  colonising: 'Start Colonising',
  colonised: 'Mark Colonised',
  storage: 'Move to Storage',
  discarded: 'Discard'
}

// Terminal statuses - editing disabled
const TERMINAL_STATUSES = ['used', 'contaminated', 'discarded']

export default function SpawnBatchDetailModal({
  isOpen,
  onClose,
  batch,
  onStatusChange,
  onStartShadow,
  onBatchUpdate,
  isLoading = false
}) {
  const navigate = useNavigate()
  const [showRecipeModal, setShowRecipeModal] = useState(false)
  const [bagToContaminate, setBagToContaminate] = useState(null)
  const [bagToMarkUsed, setBagToMarkUsed] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [addBagsOpen, setAddBagsOpen] = useState(false)
  const [bagsToAdd, setBagsToAdd] = useState('')
  const [isEditingCode, setIsEditingCode] = useState(false)
  const [editCodeValue, setEditCodeValue] = useState('')
  const [codeOverride, setCodeOverride] = useState(null)

  const isShadow = batch?.is_shadow

  // Fetch individual bags for this batch
  const { bags, loading: bagsLoading, refetch: refetchBags } = useSpawnBatchBags(batch?.id)
  const { markBagColonised, markBagContaminated, markBagUsed, loading: bagMutationLoading } = useSpawnBagMutations()
  const currentStatus = isShadow ? 'planned' : batch?.status
  const isTerminal = TERMINAL_STATUSES.includes(currentStatus)

  // Get strain ID for filtering dropdowns
  const strainId = batch?.strain_id || batch?.strains?.id

  // Mutations hook
  const { updateSpawnBatch, loading: mutationLoading } = useSpawnMutations()

  // Calculate bag stats from bags array
  const bagStats = useMemo(() => {
    const stats = { available: 0, used: 0, contaminated: 0, discarded: 0, colonised: 0 }
    bags.forEach(bag => {
      if (stats[bag.status] !== undefined) {
        stats[bag.status]++
      }
      if (bag.colonised_at) {
        stats.colonised++
      }
    })
    return stats
  }, [bags])

  // Fetch liquid cultures filtered by strain (only ready or in_use)
  const { liquidCultures } = useLiquidCultures({ strainId })
  const availableLCs = useMemo(() => {
    const filtered = (liquidCultures || []).filter(
      lc => lc.status === 'ready' || lc.status === 'in_use'
    )
    return [
      { value: null, label: 'No LC assigned' },
      ...filtered.map(lc => ({
        value: lc.id,
        label: lc.culture_code,
        badge: `G${lc.generation}`,
        mono: true
      }))
    ]
  }, [liquidCultures])

  // Fetch spawn recipes
  const { recipes } = useProcessRecipes({ recipeType: 'spawn' })
  const recipeOptions = useMemo(() => {
    return [
      { value: null, label: 'No recipe' },
      ...(recipes || []).map(r => ({
        value: r.id,
        label: r.name,
        badge: r.strain?.code || 'Generic'
      }))
    ]
  }, [recipes])

  const availableTransitions = useMemo(() => {
    if (!currentStatus) return []
    return statusTransitions[currentStatus] || []
  }, [currentStatus])

  // Handle field save with auto-save
  const handleFieldSave = useCallback(async (field, value) => {
    if (!batch?.id || isShadow) return

    try {
      await updateSpawnBatch({
        id: batch.id,
        [field]: value
      })
      // Notify parent to refresh data
      onBatchUpdate?.()
    } catch (err) {
      console.error(`[SpawnBatchDetailModal] Failed to update ${field}:`, err)
    }
  }, [batch?.id, isShadow, updateSpawnBatch, onBatchUpdate])

  // Handle inline batch code editing
  const batchCode = batch?.code || batch?.batch_code

  // Clear optimistic override once the prop catches up after refetch
  useEffect(() => {
    setCodeOverride(null)
  }, [batchCode])

  const handleCodeEdit = useCallback(() => {
    if (isTerminal || isShadow) return
    setEditCodeValue(codeOverride ?? batchCode ?? '')
    setIsEditingCode(true)
  }, [isTerminal, isShadow, batchCode, codeOverride])

  const handleCodeSave = useCallback(async () => {
    const trimmed = editCodeValue.trim()
    setIsEditingCode(false)
    if (!trimmed || trimmed === batchCode) return

    setCodeOverride(trimmed)
    try {
      await updateSpawnBatch({ id: batch?.id, code: trimmed })
      onBatchUpdate?.()
    } catch (err) {
      console.error('[SpawnBatchDetailModal] Failed to update code:', err)
      setCodeOverride(null)
      const msg = err?.message || ''
      if (msg.includes('duplicate') || msg.includes('unique') || msg.includes('already')) {
        toast.error(`Code "${trimmed}" is already in use`)
      } else {
        toast.error('Failed to rename batch code')
      }
    }
  }, [editCodeValue, batchCode, batch?.id, updateSpawnBatch, onBatchUpdate])

  const handleCodeKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleCodeSave()
    } else if (e.key === 'Escape') {
      setIsEditingCode(false)
    }
  }, [handleCodeSave])

  // Handle status change via action button
  const handleStatusButtonClick = useCallback(async (newStatus) => {
    if (isShadow && newStatus === 'colonising' && onStartShadow) {
      await onStartShadow(batch)
    } else if (onStatusChange && batch) {
      await onStatusChange(batch.id, newStatus)
    }
  }, [batch, isShadow, onStatusChange, onStartShadow])

  // Handle marking a single bag as contaminated
  const handleBagContaminate = useCallback(async (bagId) => {
    try {
      await markBagContaminated(bagId)
      refetchBags()
      onBatchUpdate?.() // Refresh parent data (bags_remaining updates via trigger)
      setBagToContaminate(null)
    } catch (err) {
      console.error('[SpawnBatchDetailModal] Failed to mark bag contaminated:', err)
    }
  }, [markBagContaminated, refetchBags, onBatchUpdate])

  // Handle marking a single bag as colonised
  const handleBagColonise = useCallback(async (bagId) => {
    try {
      await markBagColonised(bagId)
      refetchBags()
      onBatchUpdate?.()
    } catch (err) {
      console.error('[SpawnBatchDetailModal] Failed to mark bag colonised:', err)
    }
  }, [markBagColonised, refetchBags, onBatchUpdate])

  // Handle marking a single bag as used (untracked usage)
  const handleBagMarkUsed = useCallback(async (bagId) => {
    try {
      await markBagUsed(bagId)
      refetchBags()
      onBatchUpdate?.() // Refresh parent data (bags_remaining updates via trigger)
      setBagToMarkUsed(null)
    } catch (err) {
      console.error('[SpawnBatchDetailModal] Failed to mark bag as used:', err)
    }
  }, [markBagUsed, refetchBags, onBatchUpdate])

  // Handle adding bags
  const handleAddBags = useCallback(async () => {
    const count = parseInt(bagsToAdd, 10)
    if (!count || count <= 0 || !batch?.id) return

    const currentBagCount = batch.bag_count || batch.target_bags || 0
    const newBagCount = currentBagCount + count

    try {
      await updateSpawnBatch({
        id: batch.id,
        bag_count: newBagCount
      })
      refetchBags()
      onBatchUpdate?.()
      setBagsToAdd('')
      setAddBagsOpen(false)
    } catch (err) {
      console.error('[SpawnBatchDetailModal] Failed to add bags:', err)
    }
  }, [bagsToAdd, batch, updateSpawnBatch, refetchBags, onBatchUpdate])

  // Navigate to production batch when clicking on used bag
  const handleUsedBagClick = useCallback((productionBatchId) => {
    if (!productionBatchId) return
    navigate(`/production/batches/${productionBatchId}`)
    onClose()
  }, [navigate, onClose])

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null
    return parseDateString(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (!isOpen || !batch) return null

  // Normalise field names (shadow batches use different naming)
  const code = codeOverride ?? batch.code ?? batch.batch_code
  const strainName = batch.strain_name || batch.strains?.name
  const strainCode = batch.strain_code || batch.strains?.code
  const inoculationDate = batch.inoculation_date || batch.spawn_start
  const expectedReadyDate = batch.expected_ready_date
  const bagCount = batch.bag_count || batch.target_bags || batch.total_blocks
  const weightPerBag = batch.weight_per_bag || batch.bag_weight_kg
  const totalKg = bagCount && weightPerBag ? (bagCount * weightPerBag).toFixed(1) : null
  const liquidCultureId = batch.liquid_culture_id
  const recipeTemplateId = batch.recipe_template_id

  // Get current LC and recipe names for display
  const currentLC = liquidCultures?.find(lc => lc.id === liquidCultureId)
  const currentRecipe = recipes?.find(r => r.id === recipeTemplateId)

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "sm:max-w-2xl max-h-[90vh] overflow-y-auto",
          isTerminal && "opacity-70"
        )}
      >
        {/* Header - batch code + strain badge, strain name below */}
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="flex items-center gap-2 text-xl font-mono">
              {isEditingCode ? (
                <input
                  autoFocus
                  className="text-xl font-mono w-48 bg-transparent border-b border-primary outline-none"
                  value={editCodeValue}
                  onChange={(e) => setEditCodeValue(e.target.value)}
                  onBlur={handleCodeSave}
                  onKeyDown={handleCodeKeyDown}
                />
              ) : (
                <button
                  type="button"
                  onClick={handleCodeEdit}
                  disabled={isTerminal || isShadow}
                  className={cn(
                    "rounded px-1 -mx-1 transition-colors",
                    !(isTerminal || isShadow) && "hover:bg-muted/50 cursor-pointer"
                  )}
                >
                  {code}
                </button>
              )}
              {strainCode && (
                <Badge variant="outline" className="font-mono text-xs">
                  {strainCode}
                </Badge>
              )}
            </DialogTitle>
            <Badge variant="secondary" className="ml-auto">
              {currentStatus}
            </Badge>
          </div>
          <p className="text-muted-foreground">{strainName}</p>
        </DialogHeader>

        {/* Info Section - dates editable, bag summary read-only */}
        <div className="space-y-3 mt-4">
          {/* Dates row - editable */}
          <div className="grid grid-cols-2 gap-4">
            <InlineEditableField
              label="Inoculated"
              type="date"
              value={inoculationDate}
              onSave={(v) => handleFieldSave('inoculation_date', v)}
              disabled={isTerminal || isShadow}
            />

            <InlineEditableField
              label="Expected Ready"
              type="date"
              value={expectedReadyDate}
              onSave={(v) => handleFieldSave('expected_ready_date', v)}
              disabled={isTerminal || isShadow}
            />
          </div>

          {/* Bag summary - read-only */}
          <p className="text-sm text-muted-foreground">
            {bagCount || 0} bags × {weightPerBag || '?'} kg = {totalKg || '?'} kg total
          </p>
        </div>

        {/* Bag Status Stats */}
        {!isShadow && bags.length > 0 && (
          <div className="flex gap-2 mt-4">
            <div className="flex-1 text-center p-2 rounded-md bg-muted/50">
              <p className="text-lg font-semibold">{bagStats.available}</p>
              <p className="text-xs text-muted-foreground">Available</p>
            </div>
            {currentStatus === 'colonising' && bagStats.colonised > 0 && (
              <div className="flex-1 text-center p-2 rounded-md bg-green-500/10">
                <p className="text-lg font-semibold text-green-700 dark:text-green-400">{bagStats.colonised}</p>
                <p className="text-xs text-muted-foreground">Colonised</p>
              </div>
            )}
            <div className="flex-1 text-center p-2 rounded-md bg-muted/50">
              <p className="text-lg font-semibold">{bagStats.used}</p>
              <p className="text-xs text-muted-foreground">Used</p>
            </div>
            {bagStats.contaminated > 0 && (
              <div className="flex-1 text-center p-2 rounded-md bg-destructive/10">
                <p className="text-lg font-semibold text-destructive">{bagStats.contaminated}</p>
                <p className="text-xs text-muted-foreground">Contam.</p>
              </div>
            )}
          </div>
        )}

        {/* Collapsible Details Section */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen} className="mt-4">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:underline">
            {detailsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Details
            {(currentLC || currentRecipe) && !detailsOpen && (
              <span className="text-muted-foreground font-normal">
                ({currentLC?.culture_code || ''}{currentLC && currentRecipe ? ', ' : ''}{currentRecipe?.name || ''})
              </span>
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 mt-3">
            {/* LC Source dropdown */}
            <InlineSelectField
              label="Liquid Culture"
              options={availableLCs}
              value={liquidCultureId}
              onSave={(v) => handleFieldSave('liquid_culture_id', v)}
              placeholder="Select LC..."
              disabled={isTerminal || isShadow}
            />

            {/* Recipe dropdown */}
            <InlineSelectField
              label="Spawn Recipe"
              options={recipeOptions}
              value={recipeTemplateId}
              onSave={(v) => handleFieldSave('recipe_template_id', v)}
              placeholder="Select recipe..."
              disabled={isTerminal || isShadow}
              onCreateNew={() => setShowRecipeModal(true)}
              createNewLabel="Create new recipe"
            />
          </CollapsibleContent>
        </Collapsible>

        {/* Notes (if any) */}
        {batch.notes && (
          <div className="mt-4 p-3 bg-muted/50 rounded-md">
            <p className="text-xs text-muted-foreground mb-1">Notes</p>
            <p className="text-sm">{batch.notes}</p>
          </div>
        )}

        {/* Individual Bags Table */}
        {!isShadow && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium">Individual Bags</h4>
              {!isTerminal && (
                <Popover open={addBagsOpen} onOpenChange={setAddBagsOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 gap-1">
                      <Plus className="h-3.5 w-3.5" />
                      Add Bags
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-3" align="end">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Add bags</p>
                      <Input
                        type="number"
                        min="1"
                        placeholder="Number of bags"
                        value={bagsToAdd}
                        onChange={(e) => setBagsToAdd(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddBags()}
                      />
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={handleAddBags}
                        disabled={!bagsToAdd || parseInt(bagsToAdd, 10) <= 0 || mutationLoading}
                      >
                        Add
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            {bagsLoading ? (
              <p className="text-sm text-muted-foreground">Loading bags...</p>
            ) : bags.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No bag records (legacy batch)</p>
            ) : (
              <TooltipProvider>
                <div className="border rounded-md overflow-y-auto" style={{ maxHeight: '300px' }}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Bag #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead className="w-24 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bags.map((bag) => (
                        <TableRow key={bag.id}>
                          <TableCell className="font-mono">{code}-{bag.bag_number}/{bagCount}</TableCell>
                          <TableCell>
                            <Badge variant={bagStatusVariants[bag.status] || 'outline'}>
                              {bag.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {bag.status === 'used' && bag.production_batch ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    className="text-sm text-blue-600 hover:underline cursor-pointer"
                                    onClick={() => handleUsedBagClick(bag.production_batch_id)}
                                  >
                                    → {bag.production_batch.batch_code}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Used on {formatDate(bag.used_at)}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : bag.status === 'contaminated' && bag.contamination_notes ? (
                              <span className="text-xs text-muted-foreground">{bag.contamination_notes}</span>
                            ) : bag.colonised_at ? (
                              <span className="text-xs text-green-700 dark:text-green-400">Colonised {formatDate(bag.colonised_at)}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {bag.status === 'available' && !isTerminal && (
                              bagToContaminate === bag.id ? (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => handleBagContaminate(bag.id)}
                                    disabled={bagMutationLoading}
                                  >
                                    Confirm
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setBagToContaminate(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : bagToMarkUsed === bag.id ? (
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleBagMarkUsed(bag.id)}
                                    disabled={bagMutationLoading}
                                  >
                                    Confirm
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setBagToMarkUsed(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex flex-col gap-0.5 items-end">
                                  {currentStatus === 'colonising' && !bag.colonised_at && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleBagColonise(bag.id)}
                                      disabled={bagMutationLoading}
                                      className="h-7 text-green-700 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
                                    >
                                      Colonise
                                    </Button>
                                  )}
                                  <div className="flex gap-1">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setBagToMarkUsed(bag.id)}
                                          className="h-7"
                                        >
                                          Used
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Mark as used (untracked)</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setBagToContaminate(bag.id)}
                                      className="h-7 text-destructive hover:text-destructive"
                                    >
                                      Contam.
                                    </Button>
                                  </div>
                                </div>
                              )
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TooltipProvider>
            )}
          </div>
        )}

        <Separator className="my-4" />

        {/* Action buttons - right aligned */}
        <div className="flex flex-wrap items-center justify-end gap-2 w-full">
          {availableTransitions.map(status => (
            <Button
              key={status}
              variant={status === 'discarded' ? 'destructive' : 'outline'}
              onClick={() => handleStatusButtonClick(status)}
              disabled={isLoading || mutationLoading}
            >
              {statusButtonLabels[status] || `Mark ${status}`}
            </Button>
          ))}

          {availableTransitions.length === 0 && (
            <p className="text-sm text-muted-foreground">No status changes available</p>
          )}
        </div>
      </DialogContent>

      {/* Recipe Creation Modal */}
      <RecipeModal
        open={showRecipeModal}
        onOpenChange={setShowRecipeModal}
        recipeType="spawn"
        strainId={strainId}
        onSave={(recipe) => {
          handleFieldSave('recipe_template_id', recipe.id)
          setShowRecipeModal(false)
        }}
        onCancel={() => setShowRecipeModal(false)}
      />
    </Dialog>
  )
}
